import { InjectionToken } from '@angular/core';

/**
 * Everything the realtime layer needs from the host application.
 *
 * Supplied through a token rather than imported, so this library depends on nothing in the
 * rest of the workspace. The legacy SignalR services reach five levels up into the app to read
 * `environments/environment` — a library importing from an application — which is exactly the
 * coupling this avoids.
 */
export interface RealtimeConfig {
  /** Absolute URL of the hub, e.g. https://api.example.com/realtime-hub */
  readonly hubUrl: string;

  /** When false the connection is never opened. Lets the rollout be flipped without a deploy. */
  readonly enabled: boolean;

  /** Console logging of connection lifecycle. Leave off in production. */
  readonly verbose?: boolean;
}

export const REALTIME_CONFIG = new InjectionToken<RealtimeConfig>('realtime.config');

/**
 * How the library obtains a bearer token. The host adapts its own auth service to this, so the
 * library never imports one — and swapping auth implementations later touches one adapter.
 */
export interface RealtimeTokenProvider {
  /**
   * Called on every negotiate and every reconnect attempt, so it must return the *current*
   * token rather than one captured at construction. Return null when there is no session:
   * that is how the connection knows to stay closed instead of retrying against a 401.
   */
  getToken(): string | null;
}

export const REALTIME_TOKEN_PROVIDER = new InjectionToken<RealtimeTokenProvider>('realtime.tokenProvider');
